import React, { useContext, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Radar, ArrowRight, Merge, GitBranch, ArrowUpRight, Link as LinkIcon, Info } from 'lucide-react';
import { toast } from 'sonner';

// Overlap Scan — semantic-embedding pair scan for course overlap.
// Uses the local `BAAI/bge-small-en-v1.5` embedder via /api/admin/lms/overlap-scan
// so it's free and offline. Renders a similarity score + a recommended
// action ("Merge", "Position as prerequisite", "Reposition as advanced
// follow-up", "Differentiate scope", "Cross-link").

const ACTION_META = {
  'Merge':                          { cls: 'bg-rose-500/10 text-rose-800 border-rose-500/40',        Icon: Merge },
  'Position as prerequisite':       { cls: 'bg-amber-500/10 text-amber-800 border-amber-500/40',     Icon: GitBranch },
  'Reposition as advanced follow-up':{ cls: 'bg-sky-500/10 text-sky-800 border-sky-500/40',           Icon: ArrowUpRight },
  'Differentiate scope':            { cls: 'bg-violet-500/10 text-violet-800 border-violet-500/40',  Icon: Info },
  'Cross-link':                     { cls: 'bg-emerald-500/10 text-emerald-800 border-emerald-500/40', Icon: LinkIcon },
};

const OverlapScan = () => {
  const { API } = useContext(AuthContext);
  const [threshold, setThreshold] = useState(0.75);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const runScan = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/admin/lms/overlap-scan`, null, { params: { threshold } });
      setResult(r.data);
      const n = (r.data?.pairs || []).length;
      toast.success(n ? `Found ${n} overlapping pair${n === 1 ? '' : 's'}` : 'No overlap above threshold');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Overlap scan failed');
    } finally { setRunning(false); }
  };

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="overlap-scan-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight flex items-center gap-2">
            <Radar className="w-6 h-6 text-primary" />
            Course Overlap Scan
          </h1>
          <p className="text-sm text-muted-foreground max-w-xl">
            Semantic scan across every course's title + module + lesson names.
            Pairs above the threshold are surfaced with a recommended action so
            you can merge, sequence, or differentiate before students notice.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold flex items-center gap-2">
            Threshold
            <input
              type="range" min={0.5} max={0.95} step={0.05}
              value={threshold} onChange={(e) => setThreshold(Number(e.target.value))}
              className="w-28"
              data-testid="overlap-threshold-slider"
            />
            <span className="tabular-nums text-primary font-bold">{(threshold * 100).toFixed(0)}%</span>
          </label>
          <Button onClick={runScan} disabled={running} data-testid="overlap-run-btn">
            {running ? 'Scanning…' : 'Run scan'}
          </Button>
        </div>
      </div>

      {result && (
        <div className="text-xs text-muted-foreground mb-3">
          Scanned {result.course_count} courses at ≥ {(result.threshold * 100).toFixed(0)}% similarity.
        </div>
      )}

      {result && result.pairs?.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No pairs above {(result.threshold * 100).toFixed(0)}%. Your catalog is well-differentiated 🎉
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3">
        {result?.pairs?.map((p, i) => {
          const meta = ACTION_META[p.action] || ACTION_META['Cross-link'];
          return (
            <Card key={i} data-testid={`overlap-pair-${p.a.id}-${p.b.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 flex-1 min-w-64">
                    <CoursePill course={p.a} />
                    <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
                    <CoursePill course={p.b} />
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-3xl font-black leading-none tabular-nums text-primary"
                         data-testid={`overlap-sim-${p.a.id}-${p.b.id}`}>
                      {p.similarity_pct}%
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">similarity</div>
                  </div>
                </div>
                <div className={`mt-3 rounded-md border px-3 py-2 flex items-start gap-2 ${meta.cls}`}>
                  <meta.Icon className="w-4 h-4 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-bold" data-testid={`overlap-action-${p.a.id}-${p.b.id}`}>
                      Recommended: {p.action}
                    </div>
                    <div className="text-xs opacity-90 mt-0.5">{p.reason}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

const CoursePill = ({ course }) => (
  <Link
    to={`/admin/lms/course/${course.id}/workspace`}
    className="min-w-0 flex-1 rounded-md border border-border px-2.5 py-1.5 hover:border-primary hover:bg-muted/30 transition-colors"
    data-testid={`overlap-course-link-${course.id}`}
  >
    <div className="text-xs font-semibold truncate">{course.title}</div>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
      {course.tier} · {course.duration_minutes || 0}min
    </div>
  </Link>
);

export default OverlapScan;
